import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  orderBy,
  deleteDoc,
  doc,
  updateDoc,
  getDoc,
  Timestamp,
  QueryDocumentSnapshot,
  DocumentData,
  QueryConstraint,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { IUnidade as Unidade } from '../types/firestore.types';

const unidadesCollectionRef = collection(db, 'unidades');

const mapFirestoreDocToUnidade = (doc: QueryDocumentSnapshot<DocumentData>): Unidade => {
  const data = doc.data();
  return {
    id: doc.id,
    nome: data.nome,
    endereco: data.endereco,
    criado_em: data.criado_em,
    atualizado_em: data.atualizado_em,
  };
};

export const criarUnidade = async (
  unidadeData: Omit<Unidade, 'id' | 'criado_em' | 'atualizado_em'>
): Promise<Unidade> => {
  try {
    const now = Timestamp.now();
    const docRef = await addDoc(unidadesCollectionRef, {
      ...unidadeData,
      criado_em: now,
      atualizado_em: now,
    });
    return {
      id: docRef.id,
      ...unidadeData,
      criado_em: now,
      atualizado_em: now,
    };
  } catch (error) {
    console.error('Erro ao criar unidade:', error);
    throw new Error(`Falha ao criar unidade: ${(error as Error).message}`);
  }
};

export const listarUnidades = async (
  orderByField: keyof Unidade = 'nome',
  orderDirection: 'asc' | 'desc' = 'asc'
): Promise<Unidade[]> => {
  try {
    const q = query(unidadesCollectionRef, orderBy(orderByField, orderDirection));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(mapFirestoreDocToUnidade);
  } catch (error) {
    console.error('Erro ao listar unidades:', error);
    throw new Error(`Falha ao listar unidades: ${(error as Error).message}`);
  }
};

export const buscarUnidadePorId = async (id: string): Promise<Unidade | null> => {
  try {
    const docRef = doc(db, 'unidades', id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return mapFirestoreDocToUnidade(docSnap);
    } else {
      return null;
    }
  } catch (error) {
    console.error(`Erro ao buscar unidade com ID ${id}:`, error);
    throw new Error(`Falha ao buscar unidade: ${(error as Error).message}`);
  }
};

export const atualizarUnidade = async (
  id: string,
  unidadeData: Partial<Omit<Unidade, 'id' | 'criado_em'>>
): Promise<void> => {
  try {
    const docRef = doc(db, 'unidades', id);
    await updateDoc(docRef, {
      ...unidadeData,
      atualizado_em: Timestamp.now(),
    });
  } catch (error) {
    console.error(`Erro ao atualizar unidade com ID ${id}:`, error);
    throw new Error(`Falha ao atualizar unidade: ${(error as Error).message}`);
  }
};

export const excluirUnidade = async (id: string): Promise<void> => {
  try {
    const docRef = doc(db, 'unidades', id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error(`Erro ao excluir unidade com ID ${id}:`, error);
    throw new Error(`Falha ao excluir unidade: ${(error as Error).message}`);
  }
};

export const buscarUnidadesPorNome = async (nome: string): Promise<Unidade[]> => {
  try {
    const q = query(unidadesCollectionRef, where('nome', '==', nome));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(mapFirestoreDocToUnidade);
  } catch (error) {
    console.error(`Erro ao buscar unidades por nome "${nome}":`, error);
    throw new Error(`Falha ao buscar unidades por nome: ${(error as Error).message}`);
  }
};

export const buscarUnidadesPorEndereco = async (endereco: string): Promise<Unidade[]> => {
  try {
    const q = query(unidadesCollectionRef, where('endereco', '==', endereco));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(mapFirestoreDocToUnidade);
  } catch (error) {
    console.error(`Erro ao buscar unidades por endereço "${endereco}":`, error);
    throw new Error(`Falha ao buscar unidades por endereço: ${(error as Error).message}`);
  }
};

export const listarUnidadesPaginadas = async (
  limitCount: number,
  lastDocId?: string,
  orderByField: keyof Unidade = 'nome',
  orderDirection: 'asc' | 'desc' = 'asc'
): Promise<{ unidades: Unidade[]; lastVisible: QueryDocumentSnapshot<DocumentData> | null }> => {
  try {
    const queryConstraints: QueryConstraint[] = [orderBy(orderByField, orderDirection)];

    if (lastDocId) {
      const lastDocRef = doc(db, 'unidades', lastDocId);
      const lastDocSnap = await getDoc(lastDocRef);
      if (lastDocSnap.exists()) {
        queryConstraints.push(startAfter(lastDocSnap));
      }
    }

    queryConstraints.push(limit(limitCount));

    const q = query(unidadesCollectionRef, ...queryConstraints);
    const querySnapshot = await getDocs(q);

    const unidades = querySnapshot.docs.map(mapFirestoreDocToUnidade);
    const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1] || null;

    return { unidades, lastVisible };
  } catch (error) {
    console.error('Erro ao listar unidades paginadas:', error);
    throw new Error(`Falha ao listar unidades paginadas: ${(error as Error).message}`);
  }
};

// Importações adicionais para paginação (startAfter, limit)
import { startAfter, limit } from 'firebase/firestore';